/**
 * operator.js — how a human proves they are staff.
 *
 * ⚠️ WHY THIS FILE EXISTS AT ALL. In the source app the Ledger was gated by
 * `user?.role === 'admin'`: you signed into the Shaman app with your own
 * account, `user_roles` said `admin`, and the tab appeared. This platform has
 * NO ACCOUNTS. Its callers hold booth tokens minted by an unauthenticated
 * endpoint (D-04), and granting those an admin view would hand every guest's
 * portrait and email to anyone who scanned the QR code.
 *
 * So the operator credential is a shared secret in a header, compared in
 * constant time by `requireOperator()` (D-28 §3). This module is the browser
 * half: hold it, send it, forget it.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ THIS WAS sessionStorage AND IT WAS WRONG FOR THE PEOPLE WHO HAVE TO USE IT.
 *
 * The original note said: "A booth phone gets left on a table, handed to a
 * volunteer, borrowed. sessionStorage dies when the tab closes." Every word of
 * that is true and it still produced the wrong answer, because it optimised
 * against the rare bad case and ignored the constant one.
 *
 * Joel, who has worked these events, 2026-08-27: *"No employee will keep a tab
 * open all day, what if they create a new one, what if they accidentally close
 * it? ... they're already hot, wearing costumes, dealing with customers ...
 * They need to be delighted in using this and tell everyone how cool it is.
 * Not how shitty Joel's shit is because it's hard to use."*
 *
 * sessionStorage means re-entering a 48-character secret every time a tab
 * closes — which, at a hot dusty booth on a phone that sleeps, is many times a
 * day. The predictable result is not better security. It is a sticky note with
 * the secret written on it taped to the till, which is strictly worse than
 * localStorage in every way.
 *
 * So: **localStorage, with an expiry, on a device the owner set up.** Staff
 * never type it and never see it. The real controls are elsewhere and they are
 * better ones:
 *
 *   1. **Rotating `OPERATOR_SECRET` in Netlify logs out every device at once.**
 *      That is a true kill switch and it takes ten seconds — far stronger than
 *      hoping a tab got closed.
 *   2. **It expires by itself** (`TTL_DAYS`), so a device that quietly leaves
 *      the booth stops working without anyone having to notice.
 *   3. **"Forget this device" is one visible button**, for end of season or a
 *      phone that walks.
 *
 * ⚠️ STILL NOT IN THE URL. `?admin=<secret>` would be two lines less work and
 * would put the credential in browser history, in the Referer header of every
 * outbound link, and in any proxy log between the booth and here.
 *
 * ⚠️ THE GUEST'S BOOTH TOKEN IS STILL NOT STORED AT ALL (brief §4.26).
 * Different object, different rule. Nothing here changes that.
 * ═══════════════════════════════════════════════════════════════════════════
 */

const KEY = 'sp.operator';
const EXP = 'sp.operator.exp';

/* ⚠️ THE DEVICE HAS A NAME, THE PERSON DOES NOT. Joel, 2026-08-27, on staff
   logins: he wants accountability without an org chart in software. Naming the
   DEVICE - "Till 1", "Back tent", "Cammy's phone" - answers the question
   actually being asked ("which till printed that?") for a fraction of the build
   of per-person accounts, and it survives somebody quitting mid-season: you
   delete a device, not an account.

   Per-PERSON identity is a real need later - "Marcus printed forty free prints
   on his shift" is a sentence a device name cannot produce. It is not a Sep 5
   need. See Q-26. */
const NAME = 'sp.operator.device';

/* Long enough to cover a festival season without a single re-entry; short
   enough that a forgotten device does not stay unlocked forever. Sliding: it
   renews on every successful use, so a booth in daily service never expires
   and a device that stops being used does. */
const TTL_DAYS = 45;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

let memo = null;

function readStore(k) {
  try { return localStorage.getItem(k); } catch { return null; }
}
function writeStore(k, v) {
  try { localStorage.setItem(k, v); } catch { /* private mode: memory only */ }
}
function dropStore(k) {
  try { localStorage.removeItem(k); } catch { /* nothing to do */ }
}

export function getOperatorKey() {
  if (memo) return memo;

  const stored = readStore(KEY);
  if (!stored) return null;

  const expires = Number(readStore(EXP) || 0);
  if (expires && Date.now() > expires) {
    // ⚠️ Expired keys are REMOVED, not just ignored. A dead secret sitting in
    // storage is a dead secret somebody can still read off the device.
    dropStore(KEY); dropStore(EXP);
    return null;
  }

  memo = stored;
  // Sliding renewal: a booth in daily use never has to be set up twice.
  writeStore(EXP, String(Date.now() + TTL_MS));
  return memo;
}

export function setOperatorKey(secret, deviceName) {
  const v = String(secret || '').trim();
  if (!v) return false;
  memo = v;
  writeStore(KEY, v);
  writeStore(EXP, String(Date.now() + TTL_MS));
  if (deviceName) writeStore(NAME, String(deviceName).slice(0, 40));
  return true;
}

/** What this device calls itself. Never a person's name unless they typed one. */
export function operatorDeviceName() {
  return readStore(NAME) || null;
}

export function clearOperatorKey() {
  memo = null;
  dropStore(KEY); dropStore(EXP); dropStore(NAME);
}

export function hasOperatorKey() {
  return Boolean(getOperatorKey());
}

/** Days until this device forgets, or null when it is not set up. */
export function operatorDaysLeft() {
  if (!getOperatorKey()) return null;
  const expires = Number(readStore(EXP) || 0);
  if (!expires) return TTL_DAYS;
  return Math.max(0, Math.ceil((expires - Date.now()) / (24 * 60 * 60 * 1000)));
}

/** Header for the operator-only endpoints. Empty when locked. */
export function operatorHeaders() {
  const k = getOperatorKey();
  return k ? { 'x-operator-secret': k } : {};
}
